// import components
import {
  TextField,
  CircularProgress,
  AutocompleteRenderInputParams
} from '@mui/material';
import { VirtualizedAutocomplete } from 'components/shared';

// import external libraries
import { useTranslation } from 'react-i18next';

// import hooks
import { usePatientSearch } from './PatientSearch.hooks';

// import types
import { Patient } from '../types';
import { StyledPatientSearchBox } from './PatientSearch.styles';

interface PatientSearchProps {
  /**
   * Controlled by the page rather than held here, so a care receiver selected programmatically —
   * handed over from another page, for instance — shows up in the box.
   */
  value: Patient | null;
  onSelected: (patient: Patient | null) => void;
}

const getOptionLabel = (option: Patient | string) => {
  if (typeof option === 'string') return option;
  return option.name || '';
};

const PatientSearch = ({ value, onSelected }: PatientSearchProps) => {
  const { t } = useTranslation('common', { keyPrefix: 'careReceiversHome' });
  const {
    searchQuery,
    setSearchQuery,
    patients,
    isLoading,
    handlePatientChange
  } = usePatientSearch();

  const renderInput = (params: AutocompleteRenderInputParams) => (
    <TextField
      {...params}
      label={t('searchPatient')}
      variant="standard"
      size="small"
      placeholder={t('searchPatientPlaceholder')}
      slotProps={{
        input: {
          ...params.InputProps,
          endAdornment: (
            <>
              {isLoading ? (
                <CircularProgress color="inherit" size={20} />
              ) : null}
              {params.InputProps.endAdornment}
            </>
          )
        }
      }}
    />
  );

  return (
    <StyledPatientSearchBox>
      <VirtualizedAutocomplete
        autoHighlight
        value={value}
        onChange={(_, newValue) => handlePatientChange(newValue, onSelected)}
        options={patients}
        getOptionLabel={getOptionLabel}
        isOptionEqualToValue={(
          option: Patient | string,
          selected: Patient | string
        ) => {
          if (typeof option === 'string' || typeof selected === 'string')
            return false;
          return option.id === selected.id;
        }}
        onInputChange={(_, newValue) => setSearchQuery(newValue)}
        renderInput={renderInput}
        noOptionsText={
          searchQuery.length > 0 ? t('noPatients') : t('typeToSearch')
        }
        fullWidth
        size="small"
      />
    </StyledPatientSearchBox>
  );
};

export default PatientSearch;
